import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { Base64 } from 'js-base64';
import { getSetLocalStorage, readAuthTokenFromCookie } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

export const useQueryParamsInit = () => {
  const queryParams = useSearchParams();
  const userSource = useStore((state) => state.userSource);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const token = useStore((state) => state.token);
  const { trackEvent } = useReporting();
  const t = useTranslations('layout');

  const handleTiktokUserSource = () => {
    if (queryParams.get('author_id') && queryParams.get('source_content_id')) {
      const params = {
        source: 'tiktok',
        data: {
          author_id: queryParams.get('author_id'),
          source_content_id: queryParams.get('source_content_id'),
        },
      };
      localStorage.setItem('zl_tiktok_user_source', JSON.stringify(params));
      const token = readAuthTokenFromCookie();
      if (token) {
        userSource();
      }
    }
  };

  const handleShareFriendInvite = () => {
    try {
      const mark = queryParams.get('mw_reward');
      const time = queryParams.get('share_time');
      const now = Date.now();
      // 计算两个时间戳之间的差值（以毫秒为单位）
      const differenceInMilliseconds = Math.abs(now - Number(time));
      // 将毫秒转换为天数
      const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24) || 0;
      // 老数据没有拼时间的参数，不需要判断时效，新数据有拼时间的参数，就需要判断时效，这两种情况都算推荐成功的
      const valid1 = mark && !time;
      const valid2 = mark && time && differenceInDays <= 90;
      if (valid1 || valid2) {
        getSetLocalStorage.set('zl-mw_reward', mark, Number(time) + 30 * 24 * 60 * 60 * 1000);
      }
    } catch (error) {
      logger.error('addEmailClick error', error);
    }
  };

  const SDSystemEmailClick = (params) => {
    try {
      if (!params.e) {
        return;
      }
      let sdEventName = '';
      const sdEventParams: Record<string, string> = {
        position_id: params.p || '',
        email: params.e || '',
        url_path: params.url || '',
        link_type: params.link_type || '',
        link_alias: params.link_alias || '',
      };
      if (params.tpl) {
        sdEventName = 'SystemEmail_Click';
        sdEventParams.template_id = params.tpl;
      } else if (params.mp) {
        sdEventName = 'MarketingEmail_Click';
        sdEventParams.marketing_plan_id = params.mp;
      } else {
        return;
      }
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
      // 使用 pushState 更新 URL（移除 hash）
      window.history.pushState({}, '', params.url);
    } catch (e) {
      logger.error('SDSystemEmailClick error', e);
    }
  };

  const handleSystemEmailClick = () => {
    try {
      let urlHash = window.location.hash;
      // 获取到hash字段 #p=banner&tpl=1&e=dGVzdEBuZXh0bWFyLmNvbQ==
      if (urlHash && urlHash[0] === '#') {
        urlHash = urlHash.slice(1, urlHash.length);
        const list = urlHash.split('&');
        const dataObject: Record<string, string> = {};

        list.forEach((item) => {
          const [key, value] = item.split('=');
          if (key && value) {
            if (key === 'e') {
              // 特殊处理：Base64 解码 email 地址
              dataObject[key] = Base64.decode(decodeURIComponent(value));
            } else {
              dataObject[key] = decodeURIComponent(value); // 其他键值对正常解码 URL 编码
            }
          }
        });
        // 获取除hash字段外其他链接
        const url = new URL(window.location.href);
        dataObject.url = url.origin + url.pathname + url.search;
        // 拿到的dataObject数据格式为
        // e: "test@nextmar.com",p: "banner",tpl: "1",url: "http://localhost:3001/?a=1"
        SDSystemEmailClick(dataObject);
      }
    } catch (error) {
      logger.error('addEmailClick error', error);
    }
  };

  const handleAutoShowLogin = () => {
    try {
      const showLogin = queryParams.get('showLogin');
      const from = queryParams.get('from');
      const reason = queryParams.get('reason');

      // 如果用户已经登录，不需要显示登录弹窗
      if (token) {
        return;
      }

      if (showLogin === 'true') {
        // 根据来源设置不同的事件类型和情况描述
        let eventType = 'manual_login';
        let situation = '手动登录';

        if (from === 'payment') {
          eventType = 'payment_login';
          situation = '支付页面';
        } else if (from === 'checkout') {
          eventType = 'checkout_login';
          situation = '结算页面';
        }

        // 在非iframe环境中使用store方式触发登录弹窗
        toast.error(t('loginStatusInvalid'));
        setShowLoginRegisterDialog({
          show: true,
          eventType,
          situation,
          initiative: true, // 标记为主动触发
        });

        logger.info('Auto show login dialog triggered', {
          from,
          reason,
          eventType,
          situation,
        });

        // 清理URL中的登录参数，避免刷新页面重复触发
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('showLogin');
          url.searchParams.delete('from');
          url.searchParams.delete('reason');
          window.history.replaceState({}, '', url.toString());
        }
      }
    } catch (error) {
      logger.error('handleAutoShowLogin error', error);
    }
  };

  useEffect(() => {
    handleTiktokUserSource();
    handleShareFriendInvite();
    handleSystemEmailClick();
    handleAutoShowLogin();
  }, []);
};
